import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';
import { SessionInactivityService } from './session-inactivity.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const snackBar = inject(MatSnackBar);
  const router = inject(Router);
  const authStateService = inject(AuthStateService);
  const inactivityService = inject(SessionInactivityService);

  return next(req).pipe(
    tap((response) => {
      // On successful response, reset inactivity timer
      // This keeps the backend cookie (sliding expiration) and frontend timer in sync
      if (isSenderApiRequest(req.url)) {
        inactivityService.resetActivity();
      }
    }),
    catchError((error: unknown) => {
      if (isSenderApiRequest(req.url) && error instanceof HttpErrorResponse && error.status === 401) {
        // If it's the initial auth check request, let the error propagate to the service
        // The service will handle it and convert to false (unauthenticated)
        if (req.url.includes('/auth/check')) {
          return throwError(() => error);
        }

        // Only show "Session expired" message if user is accessing a protected route
        // On public/pre-auth routes, 401s are expected and should propagate to the component
        if (authStateService.isOnProtectedRoute()) {
          // It's a session expiration - show snackbar and redirect
          const snackBarRef = snackBar.open(
            'Session expired. Logging you out...',
            'Dismiss',
            { duration: 3000 }
          );

          snackBarRef.afterDismissed().subscribe(() => {
            router.navigate(['/login']);
          });

          return of();
        }

        // On public routes, let the error propagate to the component
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};


function isSenderApiRequest(url: string): boolean {
  return url.startsWith(environment.apiBaseUrl);
}
